import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { useTeam } from "../contexts/TeamContext";
import { api } from "../lib/chalkApi";
import { ChalkActionButton, UpgradeGateCard } from "../components/chalk/primitives";
import InviteParentsCard from "../components/chalk/InviteParentsCard";
import { UPGRADE_GATES } from "../lib/upgradeGates";

const SPORTS = [["baseball", "Baseball"], ["softball", "Softball"], ["soccer", "Soccer"], ["basketball", "Basketball"],
  ["football", "Football"], ["volleyball", "Volleyball"], ["other", "Other"]];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const field = { width: "100%", fontSize: 16, padding: "11px 12px", borderRadius: 10, border: "0.5px solid var(--chalk-border)", boxSizing: "border-box", background: "#fff" };
const label = { fontSize: 12, color: "var(--chalk-muted)", margin: "14px 0 6px", display: "block" };

function startQuery() {
  const live = window.location.search.replace(/^\?/, "");
  if (live) sessionStorage.setItem("chalkos.start.q", live);
  return live || sessionStorage.getItem("chalkos.start.q") || "";
}

function anonId() {
  let id = localStorage.getItem("chalk_anon_id");
  if (!id) { id = `a${Math.random().toString(36).slice(2, 12)}`; localStorage.setItem("chalk_anon_id", id); }
  return id;
}

function Hero({ p }) {
  const kit = p?.src === "voluntold_kit";
  return (
    <div style={{ background: "var(--chalk-board)", color: "var(--chalk-white)", padding: "48px 22px 28px" }}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.7 }}>ChalkOS · Free for coaches</div>
        <h1 data-testid="start-headline" style={{ fontFamily: "var(--font-display, 'Bebas Neue', sans-serif)", fontSize: 40, lineHeight: 1.05, margin: "10px 0 6px" }}>
          {kit && p.coach ? `Welcome, Coach ${p.coach}!` : "Set up your team"}
        </h1>
        <p style={{ fontSize: 15, opacity: 0.85, margin: 0 }}>
          {kit ? `Let's set up ${p.team || "your team"} — it's free.` : "Schedule, changes, and team messages in one place. Free to start."}
        </p>
      </div>
    </div>
  );
}

function PracticePicker({ p, value, onChange }) {
  const toggle = (i) => onChange({ ...value, days: value.days.includes(i) ? value.days.filter((d) => d !== i) : [...value.days, i] });
  return (
    <div data-testid="start-practices">
      <span style={label}>
        Practices{p?.practices_per_week ? ` · ${p.practices_per_week} practices a week, ${p.practice_minutes || value.minutes} min` : ""}
      </span>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {DAYS.map((d, i) => (
          <button key={d} type="button" data-testid={`start-day-${i}`} onClick={() => toggle(i)}
            style={{ padding: "8px 12px", borderRadius: 999, fontSize: 13, cursor: "pointer", border: "0.5px solid var(--chalk-border)",
              background: value.days.includes(i) ? "var(--chalk-board)" : "#fff", color: value.days.includes(i) ? "#F5F0E8" : "var(--chalk-board)",
              transition: "background-color .15s ease" }}>{d}</button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <input type="time" data-testid="start-practice-time" value={value.time} onChange={(e) => onChange({ ...value, time: e.target.value })} style={field} />
        <select data-testid="start-practice-minutes" value={value.minutes} onChange={(e) => onChange({ ...value, minutes: Number(e.target.value) })} style={field}>
          {[30, 45, 60, 75, 90, 105, 120, 150, 180].map((m) => <option key={m} value={m}>{`${m} min`}</option>)}
        </select>
      </div>
    </div>
  );
}

function Result({ res, coach, onDone }) {
  const navigate = useNavigate();
  const kit = res.kit;
  return (
    <div style={{ maxWidth: 520, margin: "0 auto", padding: "18px 18px 80px" }} data-testid="start-result">
      <h2 style={{ fontSize: 18, color: "var(--chalk-board)" }}>{res.team_name} is ready.</h2>
      {kit?.outcome === "trial_granted" && <div data-testid="start-kit-trial" style={{ fontSize: 14 }}>Your 30-day Chalkboard trial is on, through {new Date(kit.trial_ends_at).toLocaleDateString()}. No card needed.</div>}
      {kit?.outcome === "already_paid" && <div data-testid="start-kit-already" style={{ fontSize: 14 }}>You're already on Chalkboard.</div>}
      {kit?.error && <div data-testid="start-kit-error" style={{ fontSize: 14, color: "#8a3b00" }}>{kit.message}</div>}
      {res.practices?.mode === "recurring" && <div data-testid="start-practices-recurring" style={{ fontSize: 13, marginTop: 6 }}>Your weekly practices are on the schedule.</div>}
      {res.practices?.mode === "first_week" && (
        <div style={{ marginTop: 12 }} data-testid="start-practices-first-week">
          <div style={{ fontSize: 13, marginBottom: 8 }}>We added this week's practices ({res.practices.created}).</div>
          <UpgradeGateCard {...UPGRADE_GATES.recurring_events} ctaLabel="See Chalkboard options →"
            context={{ source_screen: "start", team_id: res.team_id }} onUpgrade={() => navigate("/app/upgrade")} />
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        <InviteParentsCard coach={coach} team={res.team_name} joinCode={res.join_code} teamId={res.team_id} />
      </div>
      <div style={{ marginTop: 16 }}><ChalkActionButton testId="start-go-team" label="Go to my team" onClick={onDone} /></div>
    </div>
  );
}

export default function Start() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { teams, refresh, setActive } = useTeam();
  const query = useMemo(startQuery, []);
  const kitCode = useMemo(() => new URLSearchParams(query).get("kit_code"), [query]);
  const [p, setP] = useState(null);
  const [mode, setMode] = useState("new");
  const [existingId, setExistingId] = useState("");
  const [f, setF] = useState({ name: "", sport: "baseball", team_type: "youth", age_label: "", season_start: "", roster: "" });
  const [pr, setPr] = useState({ days: [], time: "17:30", minutes: 60 });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const token = useRef(`st_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    api.startPrefill(`${query}${query ? "&" : ""}anon_id=${anonId()}`).then((d) => {
      const x = d.prefill; setP(x);
      setF((o) => ({ ...o, name: x.team || "", sport: x.sport || o.sport, team_type: x.team_type || o.team_type,
        age_label: x.age || "", season_start: x.season_start || "", roster: x.roster_size || "" }));
      if (x.practice_minutes) setPr((o) => ({ ...o, minutes: x.practice_minutes }));
    }).catch(() => setP({}));
  }, [query]);

  useEffect(() => {
    if (user && (sessionStorage.getItem("chalkos.afterLogin") || "").startsWith("/start")) sessionStorage.removeItem("chalkos.afterLogin");
  }, [user]);
  const owned = (teams || []).filter((t) => t.is_owner);
  const coachName = p?.coach || user?.first_name || "";

  function auth(path) { sessionStorage.setItem("chalkos.afterLogin", "/start"); navigate(path); }

  async function confirm() {
    if (mode === "new" && f.name.trim().length < 2) return toast.error("Give your team a name.");
    if (mode === "existing" && !existingId) return toast.error("Pick a team.");
    setBusy(true);
    const [h, m] = pr.time.split(":").map(Number);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles";
    const season = f.season_start ? new Date(`${f.season_start}T12:00:00`) : null;
    const body = {
      client_token: token.current, kit_code: kitCode || null,
      attribution: { src: p?.src, kit_sport: p?.kit_sport, ...Object.fromEntries(["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].map((k) => [k, p?.[k]])) },
      practices: pr.days.length ? { days_of_week: pr.days, hour: h, minute: m, duration_minutes: pr.minutes } : null,
    };
    if (mode === "existing") body.team_id = existingId;
    else body.team = { name: f.name.trim(), sport: f.sport, team_type: f.team_type, timezone: tz,
      season_label: season ? `${season.toLocaleString("en-US", { month: "long" })} ${season.getFullYear()}` : "Season 1",
      age_label: f.age_label || null, season_start: f.season_start || null, expected_roster_size: f.roster ? Number(f.roster) : null };
    try {
      const r = await api.startConfirm(body);
      sessionStorage.removeItem("chalkos.start.q");
      setRes(r);
      refresh && refresh();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  if (res) return <><Hero p={p} /><Result res={res} coach={coachName} onDone={() => { setActive && setActive(res.team_id); navigate("/app"); }} /></>;

  return (
    <div className="chalk-min-h-screen" style={{ background: "var(--chalk-cream, #FBF8F2)" }} data-testid="start-page">
      <Hero p={p} />
      <div style={{ maxWidth: 520, margin: "0 auto", padding: "8px 18px 80px" }}>
        {loading ? null : !user ? (
          <div style={{ display: "grid", gap: 10, marginTop: 18 }}>
            <ChalkActionButton testId="start-signup" label="Create my free coach account" onClick={() => auth("/signup?role=coach")} />
            <ChalkActionButton testId="start-signin" variant="ghost" label="I already have an account" onClick={() => auth("/login")} />
          </div>
        ) : !["coach", "league_admin"].includes(user.role) ? (
          <div data-testid="start-not-coach" style={{ marginTop: 18, fontSize: 14 }}>This page is for coaches. Ask your coach for the team's join link.</div>
        ) : (
          <>
            {owned.length > 0 && (
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                {[["new", "Create a new team"], ["existing", "Use an existing team"]].map(([k, l]) => (
                  <button key={k} type="button" data-testid={`start-mode-${k}`} onClick={() => setMode(k)}
                    style={{ flex: 1, padding: "10px", borderRadius: 999, fontSize: 13, cursor: "pointer", border: "0.5px solid var(--chalk-border)",
                      background: mode === k ? "var(--chalk-board)" : "#fff", color: mode === k ? "#F5F0E8" : "var(--chalk-board)" }}>{l}</button>
                ))}
              </div>
            )}
            {mode === "existing" ? (
              <>
                <span style={label}>Your team</span>
                <select data-testid="start-existing-team" value={existingId} onChange={(e) => setExistingId(e.target.value)} style={field}>
                  <option value="">Pick a team</option>
                  {owned.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </>
            ) : (
              <>
                <span style={label}>Team name</span>
                <input data-testid="start-team-name" value={f.name} maxLength={40} onChange={(e) => setF({ ...f, name: e.target.value })} style={field} />
                <span style={label}>Sport</span>
                <select data-testid="start-sport" value={f.sport} onChange={(e) => setF({ ...f, sport: e.target.value })} style={field}>
                  {SPORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1 }}><span style={label}>Team type</span>
                    <select data-testid="start-team-type" value={f.team_type} onChange={(e) => setF({ ...f, team_type: e.target.value })} style={field}>
                      <option value="youth">Youth</option><option value="adult">Adult</option>
                    </select></div>
                  <div style={{ flex: 1 }}><span style={label}>Age group</span>
                    <input data-testid="start-age" value={f.age_label} maxLength={12} placeholder="e.g. 7-9" onChange={(e) => setF({ ...f, age_label: e.target.value })} style={field} /></div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1 }}><span style={label}>Season starts</span>
                    <input type="date" data-testid="start-season" value={f.season_start} onChange={(e) => setF({ ...f, season_start: e.target.value })} style={field} /></div>
                  <div style={{ flex: 1 }}><span style={label}>Roster size</span>
                    <input type="number" inputMode="numeric" data-testid="start-roster" value={f.roster} min={3} max={40} onChange={(e) => setF({ ...f, roster: e.target.value })} style={field} /></div>
                </div>
              </>
            )}
            <PracticePicker p={p} value={pr} onChange={setPr} />
            <div style={{ marginTop: 20 }}>
              <ChalkActionButton testId="start-confirm-btn" label="Confirm" loading={busy} onClick={confirm} />
            </div>
            <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 8 }}>Nothing is saved until you tap Confirm. We never ask for player details here.</div>
          </>
        )}
      </div>
    </div>
  );
}
